import { useTranslation } from 'react-i18next';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import CourierDetailActionsButton from '../CourierDetailActionsButton';
import React, { FC, useEffect, useState } from 'react';
import { IconEyeHide, IconEyeShow } from 'snacks-design-system';
import PIESwitch from '@src/shared/components/PIESwitch';
import { useQueryClient } from 'react-query';
import { useUpdateTips } from '../../queries';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import {
  trackManageCourierTipsToggleOff,
  trackManageCourierTipsToggleOn,
} from '../../tracking/courier-card';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import PIENotification, { IPieNotificationMode } from '@src/shared/components/PIENotification';

import styles from './CourierSettingsTips.module.scss';

export type TRestuarantSettings = {
  showTips: boolean;
};
interface ICourierSettingsTips {
  onAction: (action: TBottomSheetAction) => void;
  onDismiss: (forceToClose?: boolean) => any;
  modeConfirmPending: boolean;
  restaurantSettings: TRestuarantSettings;
}

export const CourierSettingsTips: FC<ICourierSettingsTips> = ({
  onAction,
  onDismiss,
  modeConfirmPending,
  restaurantSettings,
}) => {
  const { t } = useTranslation();
  const [buttonStatus, setButtonStatus] = useState<{ isSwitchOn: boolean }>({
    isSwitchOn: restaurantSettings.showTips,
  });
  const [isConfirmedByUser, setIsConfirmedByUser] = useState(false);
  const [isPromising, setIsPromising] = useState<boolean>();
  const { restaurant } = useKeycloakUserState();
  const queryClient = useQueryClient();
  const { notifyInformation, notifyError } = useNotificationsActions(
    NotificationCategories.CourierManagement
  );

  const updateTips = useUpdateTips({
    onSuccess: () => {
      notifyInformation(
        `${
          buttonStatus?.isSwitchOn
            ? t('tips.NOTIFICATION_TIPS_SHOWN_SUCCESS')
            : t('tips.NOTIFICATION_TIPS_HIDE_SUCCESS')
        }`
      );

      if (buttonStatus?.isSwitchOn === true) {
        trackManageCourierTipsToggleOn(restaurant?.country as any, restaurant?.language as any);
      }

      if (buttonStatus?.isSwitchOn === false) {
        trackManageCourierTipsToggleOff(restaurant?.country as any, restaurant?.language as any);
      }

      queryClient.invalidateQueries('couriers');
      queryClient.invalidateQueries('settings');
    },

    onError: () => {
      setIsPromising(false);
    },
    onSettled: () => {
      setIsPromising(false);
      onAction({ type: EBottomSheetAction.Confirm, source: {} });
      onDismiss(true);
    },
  });

  const handleOnButtonClick = () => {
    setIsPromising(true);
    setIsConfirmedByUser(true);
    if (buttonStatus !== undefined) {
      updateTips.mutate(buttonStatus.isSwitchOn);
    }
  };

  useEffect(() => {
    if (
      !isConfirmedByUser &&
      modeConfirmPending &&
      buttonStatus?.isSwitchOn !== restaurantSettings.showTips
    )
      onAction({
        type: EBottomSheetAction.ConfirmationPending,
        source: { data: buttonStatus },
      });
  }, [modeConfirmPending]);

  return (
    <div className={styles.courierSettingsTipContainer}>
      <div className={styles.courierSettingsTipContent}>
        <PIESwitch
          label={buttonStatus?.isSwitchOn ? t('tips.TOGGLE_ON') : t('tips.TOGGLE_OFF')}
          labelPosition="right"
          onChange={(value) => setButtonStatus(value)}
          setOnByDefault={restaurantSettings.showTips}
        />
        <PIENotification
          isOpen
          showClose={false}
          mode={
            buttonStatus?.isSwitchOn ? IPieNotificationMode.Positive : IPieNotificationMode.Neutral
          }
          modInline
          onClose={() => {}}
          icon={buttonStatus?.isSwitchOn ? <IconEyeShow /> : <IconEyeHide />}
        >
          {buttonStatus?.isSwitchOn
            ? t('tips.TOOGLE_ON_DESCRIPTION')
            : t('tips.TOOGLE_OFF_DESCRIPTION')}
        </PIENotification>
      </div>
      <CourierDetailActionsButton
        buttonText={t('tips.CHANGES_CONFIRM')}
        onButtonAction={handleOnButtonClick}
        actionType={EBottomSheetAction.Confirm}
        isLoading={isPromising}
        isDisabled={!!(buttonStatus?.isSwitchOn === restaurantSettings.showTips)}
        containerCustomStyle={{ width: '100%' }}
      />
    </div>
  );
};

export default CourierSettingsTips;
